import { useState } from "react";
import { useTranslation } from "react-i18next";

import { RiskTabContent } from "src/brand/EC/components/folder/RiskTabContent";
import { RiskTabTrigger } from "src/brand/EC/components/folder/RiskTabTrigger";
import IconFolder from "src/core/assets/images/icons/folder.svg?react";
import { Spinner } from "src/core/components/custom-ui/spinner";
import { Alert, AlertDescription, AlertTitle } from "src/core/components/ui/alert";
import { Tabs, TabsContent, TabsList } from "src/core/components/ui/tabs";
import { CustomRiskLevel } from "src/core/constants/risk";
import { Roles } from "src/core/constants/roles";
import { useModulerRisk } from "src/core/hooks/folder/useModulerRisk";
import { usePermissions } from "src/core/hooks/usePermissions";
import { cn } from "src/core/lib/utils";
import { getRiskClass } from "src/core/utils/riskClass";

export const FolderCartographieTab = () => {
  const { t } = useTranslation();

  const [open, setOpen] = useState<boolean>(false);

  const {
    form,
    onSubmit,
    isSaving,
    standardRiskLevel,
    globalRiskLevel,
    forcedRiskReasonOptions,
    loading,
    currentTab,
    setCurrentTab,
    isReadOnly,
    isHighRisk,
  } = useModulerRisk({ open, setOpen, isModal: false });
  const { hasRole } = usePermissions();

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Spinner />
      </div>
    );
  }
  return (
    <div className="p-20">
      <div className="flex flex-row items-center gap-x-10 mb-20">
        <IconFolder className={cn("w-30 h-30 text-alert", getRiskClass("icon", globalRiskLevel))} />
        <div className="font-medium">{t("folder.cartographie.tab.sectionTitle")}</div>
      </div>

      <Tabs
        value={currentTab}
        onValueChange={(val) => {
          setCurrentTab(val as CustomRiskLevel);
        }}
        className="w-fit max-w-[680px]"
      >
        <TabsList className="flex flex-row items-center gap-x-20 w-full justify-start mb-20">
          {/* Bouton risque faible */}
          <RiskTabTrigger
            riskTabKey={CustomRiskLevel.LOW}
            folderRiskLevel={standardRiskLevel}
            isReadOnly={isReadOnly}
          />

          {/* Bouton risque modéré */}
          <RiskTabTrigger
            riskTabKey={CustomRiskLevel.MEDIUM}
            folderRiskLevel={standardRiskLevel}
            isReadOnly={isReadOnly}
          />

          {/* Bouton risque élevé */}
          <RiskTabTrigger
            riskTabKey={CustomRiskLevel.HIGH}
            folderRiskLevel={standardRiskLevel}
            isReadOnly={isReadOnly}
          />
        </TabsList>

        <TabsContent value={CustomRiskLevel.LOW}>
          <RiskTabContent
            riskTabKey={CustomRiskLevel.LOW}
            selectedRiskTab={standardRiskLevel}
            globalRiskLevel={globalRiskLevel}
            form={form}
            onSubmit={onSubmit}
            isLoading={isSaving}
            isReadOnly={isReadOnly}
            forcedRiskReasonOptions={forcedRiskReasonOptions}
            setOpen={setOpen}
          />
        </TabsContent>
        <TabsContent value={CustomRiskLevel.MEDIUM}>
          <RiskTabContent
            riskTabKey={CustomRiskLevel.MEDIUM}
            selectedRiskTab={standardRiskLevel}
            globalRiskLevel={globalRiskLevel}
            form={form}
            onSubmit={onSubmit}
            isLoading={isSaving}
            isReadOnly={isReadOnly}
            forcedRiskReasonOptions={forcedRiskReasonOptions}
            setOpen={setOpen}
          />
        </TabsContent>
        <TabsContent value={CustomRiskLevel.HIGH}>
          <RiskTabContent
            riskTabKey={CustomRiskLevel.HIGH}
            selectedRiskTab={standardRiskLevel}
            globalRiskLevel={globalRiskLevel}
            form={form}
            onSubmit={onSubmit}
            isLoading={isSaving}
            isReadOnly={isReadOnly}
            forcedRiskReasonOptions={forcedRiskReasonOptions}
            setOpen={setOpen}
          />
        </TabsContent>
      </Tabs>

      {hasRole(Roles.EXPERT_COMPTABLE) && isHighRisk && (
        <Alert variant="destructive" className="!m-0">
          <div className="flex flex-row items-center">
            <div className="flex flex-col">
              <AlertTitle>{t("folder.cartographie.tab.highRiskAlertTitle")}</AlertTitle>
              <AlertDescription className="font-light">
                {t("folder.cartographie.tab.highRiskAlertDescription")}
              </AlertDescription>
            </div>
          </div>
        </Alert>
      )}

      {hasRole(Roles.COLLABORATEUR) && (
        <Alert variant="destructive" className="!m-0">
          <div className="flex flex-row items-center">
            <div className="flex flex-col">
              <AlertTitle>{t("folder.cartographie.tab.validationAlertTitle")}</AlertTitle>
              <AlertDescription className="font-light">
                {t("folder.cartographie.tab.validationAlertDescription")}
              </AlertDescription>
            </div>
          </div>
        </Alert>
      )}
    </div>
  );
};
